// Angular
import { BrowserModule, HAMMER_GESTURE_CONFIG, HammerGestureConfig, HammerModule } from '@angular/platform-browser';
import { APP_INITIALIZER, ErrorHandler, Injectable, NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { OverlayModule } from '@angular/cdk/overlay';

// Environment
import { environment } from '../environments/environment';

// Components
import { AppComponent } from './app.component';

// Modules
import { AuthModule } from './modules/auth/auth.module';
import { AppRoutingModule } from './app-routing.module';
import { CoreModule } from './core.module';
import { SharedModule } from './shared';
import { DialogsModule } from './dialogs/dialogs.module';
import { UsersModule } from './modules/user/user.module';

// Interceptors
import { TokenInterceptor } from './shared/interceptors/token.interceptor';
import { ErrorInterceptor } from './shared/interceptors/error.interceptor';
import { JsonInterceptor } from './shared/interceptors/json-intercept.service';

// Config
import { LayoutConfig } from './shared/config/layout.config';

// Services
import { AuthService } from './modules/auth';
import { DialogService } from './shared/services/dialog.service';
import { AppSettingsService, ErrorHandlerService } from './shared/services';
import { SplashScreenService } from './shared/components/splash-screen/services/splash-screen.service';
import { HttpUtilsService } from './shared/services/http-utils.service';
import { TypesUtilsService } from './shared/services/types-utils.service';
import { LayoutConfigService } from './shared/services/layout-config.service';
import { PageConfigService } from './shared/services/page-config.service';
import { SubheaderService } from './shared/components/subheader/services/subheader.service';
import { HeaderService } from './shared/components/header/services/header.service';
import { SideNavService } from './shared/components/side-nav/services/side-nav.service';
import { MissingTranslationHandlerService } from './shared/services/missing-translation-handler.service';

// Guards
import { CanDeactivateGuard, PasswordGuard, EnvironmentGuard } from './shared/guards';

// Libraries
import { OnboardingModule, OnboardingService } from '@rosen-group/ngx-onboarding';
import { HotkeyModule } from 'angular2-hotkeys';
import Hammer from 'hammerjs';
import { NgxsModule } from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { MissingTranslationHandler, TranslateModule } from '@ngx-translate/core';
import { NgxPermissionsModule } from 'ngx-permissions';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import 'hammerjs';

// Store
import { AuthState } from './modules/auth/state/auth.state';
import { UserAuthState } from './modules/auth/state/user-auth.state';
import { JobState } from './modules/job/state/job.state';
import { ChannelState } from './modules/channel/state/channel.state';
import { DeviceState } from './modules/asset/device/state/device.state';
import { UserState } from './modules/user/state/user.state';
import { UnitState } from './modules/asset/unit/state/unit.state';
import { DeviceUnitState } from './modules/asset/unit/state/device-unit.state';
import { ReelState } from './modules/asset/reel/state/reel.state';
import { StringState } from './modules/asset/string/state/string.state';
import { BopState } from './modules/asset/bop/state/bop.state';
import { DistrictState } from './modules/district/state/district.state';
import { CompanyState } from './modules/company/state/company.state';
import { CommentDataState } from './modules/comment/state/comment.state';
import { UnitConfigState } from './modules/unit-config/state/unit-config.state';
import { OutputConfigState } from './modules/output-config/state/output-config.state';
import { StorageState } from './shared/state/storage.state';
import { LookupState } from './shared/state/lookup.state';
import { UnitOfMeasureState } from './modules/unit-of-measure/state/unit-of-measure.state';
import { DashboardState } from './modules/job/components/dashboard/state/dashboard.state';

@Injectable()
export class MyHammerConfig extends HammerGestureConfig {
	buildHammer(element: HTMLElement) {
		return new Hammer(element, {
			touchAction: 'pan-y',
		});
	}
}

export function initializeLayoutConfig(layoutConfigService: LayoutConfigService) {
	// initialize app by loading default demo layout config
	return () => {
		if (layoutConfigService.getCurrentConfig() === null) {
			layoutConfigService.loadConfigs(new LayoutConfig().getDefaultConfigs);
		}
	};
}

@NgModule({
	declarations: [AppComponent],
	imports: [
		BrowserModule,
		BrowserAnimationsModule,
		AppRoutingModule,
		HttpClientModule,
		HammerModule,
		[],
		NgxPermissionsModule.forRoot(),
		HotkeyModule.forRoot(),

		CoreModule,
		SharedModule,
		DialogsModule,
		OverlayModule,
		UsersModule,
		NgxsModule.forRoot([AuthState, UserAuthState, JobState, UserState, ChannelState, DeviceState, DistrictState, UnitState, DeviceUnitState, ReelState, StringState, BopState, CompanyState, CommentDataState, UnitConfigState, OutputConfigState, StorageState, LookupState, UnitOfMeasureState, DashboardState], {
			developmentMode: !environment.production,
			selectorOptions: {
				suppressErrors: true,
				injectContainerState: false,
			},
		}),
		NgxsReduxDevtoolsPluginModule.forRoot({
			disabled: environment.production, // Set to true for prod mode
			// maxAge: 10,
		}),
		NgxsStoragePluginModule.forRoot({
			keys: [AuthState, StorageState],
		}),
		AuthModule.forRoot(),
		TranslateModule.forRoot({
			missingTranslationHandler: {
				provide: MissingTranslationHandler,
				useClass: MissingTranslationHandlerService,
			},
			useDefaultLang: false,
		}),
		MatProgressSpinnerModule,
		OnboardingModule,
	],
	exports: [],
	providers: [
		AuthService,
		LayoutConfigService,
		PageConfigService,
		DialogService,
		SplashScreenService,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: TokenInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: ErrorInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: JsonInterceptor,
			multi: true,
		},
		{
			// Hammer instantiation with custom config.
			// Adding because SwipeLeft was disabling scroll in JobChartComponent.
			provide: HAMMER_GESTURE_CONFIG,
			useClass: MyHammerConfig,
		},
		{
			// layout config initializer
			provide: APP_INITIALIZER,
			useFactory: initializeLayoutConfig,
			deps: [LayoutConfigService],
			multi: true,
		},
		{
			provide: ErrorHandler,
			useClass: ErrorHandlerService,
		},
		// template services
		SubheaderService,
		HeaderService,
		SideNavService,
		HttpUtilsService,
		TypesUtilsService,
		AppSettingsService,
		CanDeactivateGuard,
		EnvironmentGuard,
		PasswordGuard,
	],
	bootstrap: [AppComponent],
})
export class AppModule {
	constructor(onboardingService: OnboardingService) {
		onboardingService.configure({});
	}
}
